import { useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Outlet } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import { toast } from 'sonner';

import { ToastError, ToastSuccess } from '../../common/utils';
import {
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import useModelStore from '../../hooks/zustand/useModelStore';
import WaitingModelOutputDialog from '../WaitingModelOutputDialog';

const AuthorizedModelRoute = () => {
  const queryClient = useQueryClient();

  const [openWaitingModelOutputDialog, setOpenWaitingModelOutputDialog] =
    useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const location = useLocation();

  const isActual = searchParams.get('isActualData') === 'true';

  const { projectId, orgId } = useGetRouteParams();

  const isRunningModelOutputClient = useModelStore(
    (state) => state.isRunningModelOutput
  );

  const updateRunningModelOutput = useModelStore(
    (state) => state.updateIsRunningModelOutput
  );

  const {
    model,
    modelOutput,
    isFetchedModel,
    isLoading,
    isRunningModelOutputServer,
    isFetchedModelOutputError,
  } = useGetDistinguishModel({
    isActual,
  });

  useEffect(() => {
    if (isRunningModelOutputServer) {
      updateRunningModelOutput(true);
    }
  }, [isRunningModelOutputServer]);

  useEffect(() => {
    const isCompleteRunning =
      isRunningModelOutputClient === true &&
      isRunningModelOutputServer === false;

    if (
      isFetchedModelOutputError &&
      isCompleteRunning &&
      model &&
      _.isEmpty(modelOutput)
    ) {
      queryClient.invalidateQueries({
        queryKey: [
          'getModelOutput',
          'getReceivedModelOutput',
          {
            projectId,
            orgId,
            modelId: model.id,
          },
        ],
      });

      updateRunningModelOutput(null);

      toast.error(
        <ToastError message="There's some trouble running the model" />
      );
      return;
    }

    if (
      !isFetchedModelOutputError &&
      !_.isEmpty(modelOutput) &&
      model &&
      isCompleteRunning
    ) {
      queryClient.invalidateQueries({
        queryKey: [
          'getModelOutput',
          'getReceivedModelOutput',
          {
            projectId,
            orgId,
            modelId: model.id,
          },
        ],
      });

      updateRunningModelOutput(null);

      toast.success(
        <ToastSuccess
          message={
            <>
              <p>Model {model.name} calculation successful.</p>
            </>
          }
        />
      );
    }
  }, [
    isRunningModelOutputServer,
    isRunningModelOutputClient,
    isFetchedModelOutputError,
    _.isEmpty(modelOutput),
  ]);

  if (!model && isFetchedModel && !isLoading) {
    return (
      <Navigate
        to={`/${orgId}/projects/${projectId}/models/`}
        state={{ from: location }}
        replace
      />
    );
  }

  return (
    <div className="w-full">
      <Outlet />
      <WaitingModelOutputDialog
        open={openWaitingModelOutputDialog}
        handleClose={() => {
          setOpenWaitingModelOutputDialog(false);
        }}
      />
    </div>
  );
};

export default AuthorizedModelRoute;
